"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VGSProvider = void 0;
const tokenization_provider_abstract_1 = require("../providers/tokenization-provider.abstract");
const vgs_config_1 = require("../config/vgs.config");
const error_types_contants_1 = require("../constants/error-types.contants");
const tokenization_providers_constants_1 = require("../constants/tokenization-providers.constants");
const mappers_1 = require("../utils/mappers");
const defaultStyles = {
    width: '100%',
    height: '100%',
    border: '0px'
};
class VGSProvider extends tokenization_provider_abstract_1.TokenizationProvider {
    init() {
        return new Promise((resolve, reject) => {
            this.dom
                .loadVGSScript()
                .then(() => {
                if (VGSCollect) {
                    this.initializeVGS();
                    resolve();
                }
                else {
                    reject();
                }
            })
                .catch(() => reject());
        });
    }
    tokenizeCreditCard(options, email) {
        return new Promise((resolve, reject) => {
            if (!this.isValidData()) {
                this.notifyChangeState();
                return reject(mappers_1.mapTokenizerErrorResponse('lib', { message: error_types_contants_1.ErrorTypes.NEED_FILL_ALL_FIELDS }, 0));
            }
            this.vgsForm.submit('/payment-vault/auth/token/VGS', {
                data: this.getVGSRequest(email),
                headers: {
                    'authorization': `Bearer ${options.userToken}`,
                    'country-code': options.countryCode
                },
                mapDotToObject: 'merge',
            }, (status, response) => {
                switch (status) {
                    case 200:
                        resolve(this.mapResponse(response));
                        break;
                    case 401:
                        reject(mappers_1.mapTokenizerErrorResponse('provider', { message: error_types_contants_1.ErrorTypes.VGS_UNAUTHORIZED }, status));
                        break;
                    default:
                        reject(mappers_1.mapTokenizerErrorResponse('provider', response, status));
                }
            }, (error) => reject(mappers_1.mapTokenizerErrorResponse('provider', error, 400)));
        });
    }
    /* eslint-disable @typescript-eslint/no-unused-vars */
    getBinInfo(email) {
        return new Promise((resolve, reject) => {
            if (this.isValidData() && this.bin && this.lastFourDigits) {
                resolve({
                    bin: this.bin,
                    lastFourDigits: this.lastFourDigits
                });
            }
            else {
                this.notifyChangeState();
                return reject(mappers_1.mapTokenizerErrorResponse('lib', { message: error_types_contants_1.ErrorTypes.NEED_FILL_ALL_FIELDS }, 0));
            }
        });
    }
    reset() {
        if (this.vgsForm) {
            this.vgsForm.reset();
            this.clearForm();
        }
    }
    removeHandlers() {
        this.eventBus.removeEventListener();
    }
    initializeVGS() {
        this.notifyCardType('default');
        this.notifyChangeState();
        const vaultId = this.isProduction ? vgs_config_1.VGSConfig.VAULT_ID_PROD : vgs_config_1.VGSConfig.VAULT_ID_DEV;
        const environment = this.isProduction ? vgs_config_1.VGSConfig.ENVIRONMENT_PROD : vgs_config_1.VGSConfig.ENVIRONMENT_DEV;
        this.vgsForm = VGSCollect.create(vaultId, environment, (state) => {
            const cardNumberState = state[this.cardNumber.name];
            const cvvState = state[this.cvv.name];
            if (cardNumberState) {
                this.bin = cardNumberState.bin;
                this.lastFourDigits = cardNumberState.last4;
                this.tokenManagerState[this.cardNumber.name] = {
                    error: this.getVGSError(cardNumberState.errorMessages[0], cardNumberState.isDirty),
                    isValid: cardNumberState.isValid,
                    isFocused: cardNumberState.isFocused,
                    value: ''
                };
                this.notifyChangeState();
                this.notifyCardType(cardNumberState.cardType);
            }
            if (cvvState) {
                this.tokenManagerState[this.cvv.name] = {
                    error: this.getVGSError(cvvState.errorMessages[0], cvvState.isDirty),
                    isValid: cvvState.isValid,
                    isFocused: cvvState.isFocused,
                    value: ''
                };
                this.notifyChangeState();
            }
        });
        this.vgsForm.field(`#${this.cardNumber.elementId}`, {
            type: 'card-number',
            name: this.cardNumber.name,
            validations: ['required', 'validCardNumber'],
            autoFocus: true,
            css: defaultStyles,
            placeholder: this.cardNumber.placeholder
        });
        this.vgsForm.field(`#${this.cvv.elementId}`, {
            type: 'card-security-code',
            name: this.cvv.name,
            validations: ['required', 'validCardSecurityCode'],
            css: defaultStyles,
            placeholder: this.cvv.placeholder
        });
        this.createInputs();
    }
    getVGSRequest(email) {
        const [month, year] = this.expirationDateInput.value.split('/');
        return {
            email,
            first_six_digits: this.bin,
            last_four_digits: this.lastFourDigits,
            month: month.trim(),
            year: year.trim(),
            first_name: this.firstNameInput.value,
            last_name: this.lastNameInput.value,
            is_debit: !!this.isDebitInput ? this.isDebitInput.checked : false
        };
    }
    mapResponse(response) {
        const data = {
            credit_card_data: {
                card_cvv: response.card_cvv,
                card_number: response.card_number,
                first_six_digits: response.first_six_digits,
                last_four_digits: response.last_four_digits,
                email: response.email,
                first_name: response.first_name,
                last_name: response.last_name,
                month: response.month,
                year: response.year,
                is_debit: !!this.isDebitInput ? this.isDebitInput.checked : false,
                unique_token: response.unique_token,
                token_type: response.token_type
            },
            tokenizer: tokenization_providers_constants_1.TokenizationProviders.VGS,
            email: response.email
        };
        if (this.documentValue && this.documentType && this.documentValueInput && this.documentTypeSelect) {
            data.document = {
                document_type_id: this.documentTypeSelect.value || '',
                document_value: this.documentValueInput.value || '',
            };
        }
        return data;
    }
    getVGSError(error, isDirty) {
        if (!isDirty) {
            return error_types_contants_1.ErrorTypes.EMPTY;
        }
        switch (error) {
            case "is required" /* required */: return error_types_contants_1.ErrorTypes.IS_REQUIRED;
            case "is not a valid card number" /* validCardNumber */: return error_types_contants_1.ErrorTypes.INVALID_CARD_NUMBER;
            case "is not a valid security code" /* validCardSecurityCode */: return error_types_contants_1.ErrorTypes.INVALID_CVV;
            default: return error_types_contants_1.ErrorTypes.EMPTY;
        }
    }
}
exports.VGSProvider = VGSProvider;
